import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, RotateCcw } from 'lucide-react';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import {
  EvalKpi, FindingsPanel, Note, ScreenField, ScreenFilters,
} from '../../components/performance/evaluation/EvaluationParts';
import {
  formatNum, listOf, metaOfContext, metricByCode, useScreenParams,
} from '../../components/performance/evaluation/evaluationAnalytics';
import { employeeAsync } from '../performance-actions/ActionFormDrawer';
import { Grid, PageEmpty, PageWrap } from '../performance-operations/styles';
import {
  anyStale, fetchPeriods, oldestCalculatedAt, usePanel,
} from '../performance-operations/config';
import { BandDistributionPanel, PeerComparisonPanel } from './components/ManagerCharts';
import ManagersTable from './components/ManagersTable';
import {
  MIN_TEAM_OPTIONS, NS, Q, REVIEW_STATUSES, SCREEN_KEYS, SIGNALS, exportManagers, fetchFindings, fetchManagers, fetchSummary, screenFilters,
} from './config';

// PM Analitik P4 — E07 Yönetici Değerlendirme Davranışı (/performance/rater-behavior) — YALNIZ İK (rota + menü
// requiresEnterpriseAdmin; sunucu yönetici için de 403). Puanlama standardizasyonu ve süreç takibi: M05, M22 (benzer grup sapması;
// kendi ekibi kıyastan çıkar), M23, M24, M25. ETİK SINIR: yöneticiler arasında sıralama/bileşik kalite puanı yoktur; sinyaller
// inceleme sinyalidir, etiket değildir. Grafikler tablonun görünen sayfasını gösterir (sıra = ad sırası).
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: -4px 0 16px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const staticLookup = (options) => ({
  onSearch: async (q) => options.filter((o) => !q || o.name.toLocaleLowerCase().includes(String(q).toLocaleLowerCase())),
  onGetById: async (id) => options.find((o) => o.id === id) || { id, name: '' },
});

export default function PerformanceRaterBehaviorPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [values, setScreen] = useScreenParams(SCREEN_KEYS);
  const screen = useMemo(() => screenFilters(values), [values]);
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const filterKey = JSON.stringify([filters, screen]);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);

  const summary = usePanel(() => fetchSummary(filters, screen), [filterKey], ready);
  const findings = usePanel(() => fetchFindings(filters, screen), [filterKey], ready);
  useEffect(() => { if (summary.data) onMeta('summary', metaOfContext(summary.data)); }, [summary.data, onMeta]);

  // Yönetici tablosu ve iki grafik AYNI sayfa isteğini paylaşır (grafik = tablonun görünen sayfası).
  const [table, setTable] = useState({ key: filterKey, pageNumber: 1, pageSize: 10, sort: null, order: 'asc' });
  const effective = table.key === filterKey ? table : { key: filterKey, pageNumber: 1, pageSize: table.pageSize, sort: null, order: 'asc' };
  const tableKey = JSON.stringify(effective);
  const managers = usePanel(() => fetchManagers(filters, screen, effective), [tableKey], ready);
  const onTable = useCallback((next) => setTable({ key: filterKey, ...next }), [filterKey]);
  const exportFn = () => exportManagers(filters, screen, { sort: effective.sort, order: effective.order });

  const reviewLookup = useMemo(() => staticLookup(REVIEW_STATUSES.map((x) => ({ id: x, name: t(`${NS}.reviewStatus.${x}`) }))), [t]);
  const signalLookup = useMemo(() => staticLookup(SIGNALS.map((x) => ({ id: x, name: t(`${NS}.signal.${x}`) }))), [t]);
  const minTeamOptions = MIN_TEAM_OPTIONS.map((x) => ({ id: x, name: x }));

  const onFinding = (f) => {
    const af = f.actionFilter || {};
    if (af['signal[in]']) setScreen({ [Q.signals]: listOf(af['signal[in]']) });
    window.setTimeout(() => document.querySelector('[data-panel="managers"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  const s = summary.data;
  const context = s?.context;
  const m = (code) => metricByCode(s?.metrics, code);
  const common = { ns: NS, loading: summary.loading, error: summary.error, onRetry: summary.retry };
  const screenActive = SCREEN_KEYS.filter((k) => values[k]);
  const chipText = (k) => {
    const list = listOf(values[k]);
    switch (k) {
      case Q.rater: return t(`${NS}.filter.rater`);
      case Q.minTeamN: return `${t(`${NS}.filter.minTeamN`)}: ${values[k]}`;
      case Q.reviewStatuses: return `${t(`${NS}.filter.reviewStatus`)}: ${list.map((x) => t(`${NS}.reviewStatus.${x}`)).join(', ')}`;
      case Q.signals: return `${t(`${NS}.filter.signal`)}: ${list.map((x) => t(`${NS}.signal.${x}`)).join(', ')}`;
      default: return k;
    }
  };

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t(`${NS}.title`) }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.operations.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={oldestCalculatedAt(metas)}
          stale={anyStale(metas)}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <>
          <ScreenFilters data-filters="screen">
            <ScreenField $w="260px">
              <AsyncGenericSelect
                label={t(`${NS}.filter.rater`)} value={values[Q.rater] || null} minChars={0} compact
                onChange={(v) => setScreen({ [Q.rater]: v || null })} {...employeeAsync}
              />
            </ScreenField>
            <ScreenField $w="160px">
              <GenericSelect
                label={t(`${NS}.filter.minTeamN`)} value={values[Q.minTeamN] || null} options={minTeamOptions} idKey="id" labelKey="name"
                placeholder={t(`${NS}.filter.minTeamNDefault`, { k: context?.threshold ?? 5 })} onChange={(v) => setScreen({ [Q.minTeamN]: v || null })}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.reviewStatus`)} value={listOf(values[Q.reviewStatuses])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.reviewStatuses]: v || [] })} {...reviewLookup}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.signal`)} value={listOf(values[Q.signals])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.signals]: v || [] })} {...signalLookup}
              />
            </ScreenField>
          </ScreenFilters>
          {screenActive.length > 0 ? (
            <Chips>
              {screenActive.map((k) => (
                <Chip key={k} data-chip={k}>
                  {chipText(k)}
                  <Button variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={() => setScreen({ [k]: null })}>
                    <X size={12} />
                  </Button>
                </Chip>
              ))}
              <Button variant="ghost" data-action="screen-reset" onClick={() => setScreen(Object.fromEntries(SCREEN_KEYS.map((k) => [k, null])))}>
                <RotateCcw size={14} />
                {' '}
                {t(`${NS}.filter.clear`)}
              </Button>
            </Chips>
          ) : null}
          <Note data-note="ethics">{t(`${NS}.ethicsNote`)}</Note>
          <Grid key={filters.periodId}>
            <EvalKpi {...common} code="M05" title={t(`${NS}.kpi.M05`)} metric={m('M05')} />
            <EvalKpi
              {...common} code="pm.raterReview" title={t(`${NS}.kpi.review`)} metric={m('pm.raterReview')}
              sub={s ? t(`${NS}.kpi.reviewSub`, {
                computable: formatNum(s.managersComputable, lang, 0), total: formatNum(s.managersTotal, lang, 0), threshold: formatNum(s.deviationThreshold, lang),
              }) : undefined}
            />
            <EvalKpi {...common} code="M23" title={t(`${NS}.kpi.M23`)} metric={m('M23')} />
            <EvalKpi
              {...common} code="M25" title={t(`${NS}.kpi.M25`)} metric={m('M25')} signed
              sub={s && !s.calibrationApplicable ? t(`${NS}.kpi.calibrationNotApplicable`) : undefined}
            />
            <PeerComparisonPanel panel={managers} scaleMin={context?.scaleMin} scaleMax={context?.scaleMax} />
            <BandDistributionPanel panel={managers} />
            <FindingsPanel ns={NS} panel={findings} span={12} onAction={onFinding} />
            <ManagersTable
              filters={filters} panel={managers} table={effective} onTable={onTable} exportFn={exportFn}
              calibrationApplicable={!!s?.calibrationApplicable}
            />
          </Grid>
        </>
      ) : null}
    </PageWrap>
  );
}
